import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  createNewDataset,
  createNewDatasetV2,
  fetchDatasetPreviews,
  fetchFolderMetadata,
  selectDatasetFolders,
  startFolderUploadTransaction,
  startMultipleFolderUploadTransaction,
} from "redux/CreateDataset/actions";
import useFolderValidation from "./useFolderValidation";
import { getIsNewCreationEnabled } from "redux/CreateDataset/selectors";
import { Form } from "antd";

export default function useCreateDataset() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const { validateFolders, validateFoldersPaths } = useFolderValidation();
  const isNewCreationEnabled = useSelector(getIsNewCreationEnabled);

  const createDatasetHandler = async (name?: string) => {
    if (isNewCreationEnabled) {
      // Use new workflow action creator
      return await dispatch(createNewDatasetV2() as any);
    } else {
      // Use old dataset action creator
      return await dispatch(createNewDataset(name || "") as any);
    }
  };

  const startFoldersPathsHandler = async (paths: string[]) => {
    await validateFoldersPaths(paths);
    dispatch(fetchFolderMetadata(newDatasetId!) as any);
    form.resetFields();
  };

  const startFolderUpload = async (files: File[]) => {
    try {
      // Validate folders using the validation hook
      const validationResults = await validateFolders(files);

      if (!validationResults) {
        return; // Validation failed
      }

      const { validFolders } = validationResults;

      // Only proceed with valid folders
      if (validFolders.length === 0) {
        return;
      }

      if (validFolders.length === 1) {
        // Single folder scenario (browser selection OR single drag&drop folder)
        const folderGroup = validFolders[0];
        await dispatch(
          startFolderUploadTransaction(
            newDatasetId!,
            folderGroup.folderName,
            folderGroup.files
          ) as any
        );
      } else {
        // Multiple folders scenario (only possible via drag&drop)
        await dispatch(
          startMultipleFolderUploadTransaction(
            newDatasetId!,
            validFolders
          ) as any
        );
      }
    } catch (error: any) {
      let errorMessage: string =
        error.response?.data?.message ||
        "There was an error starting the folder upload transaction";
      console.error(errorMessage);
    }
  };

  const selectFolders = async (folderIds: string[]) => {
    if (!newDatasetId) {
      throw new Error("No dataset ID available");
    }

    return await dispatch(selectDatasetFolders(newDatasetId, folderIds) as any);
  };

  const fetchPreviews = async () => {
    return await dispatch(fetchDatasetPreviews(newDatasetId!) as any);
  };

  return {
    createNewDataset: createDatasetHandler,
    startFolderUpload,
    selectFolders,
    fetchPreviews,
    startFoldersPathsHandler,
    form
  };
}
